// FIXME
// eslint-disable-next-line workspaces/require-dependency
import type { ThreadWithValueId } from '@databases/comments/type';
import { logWarning } from '@databases/logs';
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux';
// eslint-disable-next-line workspaces/require-dependency
import { getDatabaseWithCurrentView } from '@databases/redux/database-view';

import {
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  isBrokenDatabase,
} from '../../database/utils';
import { getValueId } from '../../utils';
import { selectDatabaseByContentIdOptional } from '../databases/selectors';

export function selectThreadByValueIdOptional(
  state: DefaultRootState,
  { contentId, fieldId, entryId }: { contentId: string; fieldId: string; entryId: string }
) {
  const database = selectDatabaseByContentIdOptional(state, { contentId });

  if (!database || isBrokenDatabase(database)) {
    return undefined;
  }

  const entry = getDatabaseEntryOptional(database, entryId);
  const field = getDatabaseFieldOptional(database, fieldId);

  if (!entry || !field) {
    return undefined;
  }

  const commentThreads = database.commentThreads;

  const valueId = getValueId(entryId, fieldId);
  if (valueId in commentThreads) {
    return commentThreads[valueId];
  }

  return undefined;
}
export function selectUnresolvedThreadsByValueIdOptional(
  state: DefaultRootState,
  { contentId, fieldId, entryId }: { contentId: string; fieldId: string; entryId: string }
) {
  const threads = selectThreadByValueIdOptional(state, { contentId, fieldId, entryId });
  if (threads) {
    return threads.filter((thread) => !thread.isResolved);
  }

  return undefined;
}

export function selectValueIdFromThreadIdOptional(
  state: DefaultRootState,
  { contentId, threadId }: { contentId: string; threadId: string }
): string | undefined {
  const database = selectDatabaseByContentIdOptional(state, { contentId });

  if (!database || isBrokenDatabase(database)) {
    return undefined;
  }

  const commentThreads = database.commentThreads;

  for (const [key, value] of Object.entries(commentThreads)) {
    if (value.find((thread) => thread.threadId === threadId)) {
      return key;
    }
  }

  return undefined;
}

export function selectAllVisibleUnresolvedThreads(
  state: DefaultRootState,
  { contentId }: { contentId: string }
): ThreadWithValueId[] {
  try {
    const database = getDatabaseWithCurrentView(state, { contentId });

    if (!database || isBrokenDatabase(database)) {
      return [];
    }

    const orderedThreads = database.entries.flatMap((entryId) =>
      database.fields.flatMap((fieldId) => {
        const threads = selectThreadByValueIdOptional(state, {
          contentId,
          fieldId,
          entryId,
        });
        return threads
          ? threads
              .filter((thread) => !thread.isResolved)
              .map((thread) => ({
                ...thread,
                valueId: getValueId(entryId, fieldId),
              }))
          : [];
      })
    );
    return orderedThreads;
  } catch (e: unknown) {
    if (e instanceof Error) {
      logWarning('Error selecting visible unresolved threads', e);
    }
    return [];
  }
}

export const selectUnresolvedThreadsCommentCount = (
  threads:
    | {
        threadId: string;
        isResolved: boolean;
        commentCount: number;
        versionNumber: number;
      }[]
    | undefined
): number => {
  const totalCommentCount =
    threads?.reduce((acc, thread) => {
      return acc + thread.commentCount;
    }, 0) ?? 0;

  return totalCommentCount;
};
